import { createApi } from '@reduxjs/toolkit/query/react';

import { BugReportStatus } from '../../shared/constants';
import { IAppFile } from '../../types/interfaces/app-file';
import { IBugReport } from '../../types/interfaces/bug-report';
import { IBugReportMessage } from '../../types/interfaces/bug-report-message';
import { IBugReportType } from '../../types/interfaces/bug-report-type';
import { BUG_REPORTS, HTTP_METHOD, REDUX_CACHE } from '../constants';
import * as endpoints from '../endpoints';
import {
  TransformedErrorResponseWithNotification,
  getBaseQuery,
  getListProvidesTags,
} from '../generic';

export const bugReportApi = createApi({
  reducerPath: 'bugReportApi',
  tagTypes: [
    BUG_REPORTS.BUG_REPORT,
    BUG_REPORTS.BUG_REPORTS,
    BUG_REPORTS.BUG_REPORT_TYPES,
    REDUX_CACHE.ABORT_INVALIDATION,
    REDUX_CACHE.LIST,
  ],
  baseQuery: getBaseQuery(endpoints.BugReports.ROOT),
  // global configuration for the api
  keepUnusedDataFor: 60,
  endpoints: (builder) => ({
    getMyBugReports: builder.query<IBugReport[], void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: '',
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, BUG_REPORTS.BUG_REPORTS),
      ...TransformedErrorResponseWithNotification(),
    }),
    getBugReportTypes: builder.query<IBugReportType[], void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.BugReports.TYPES,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, BUG_REPORTS.BUG_REPORT_TYPES),
      ...TransformedErrorResponseWithNotification(),
    }),
    getBugReportByIdApi: builder.query<IBugReport, string>({
      keepUnusedDataFor: 3600,
      query(id: string) {
        return {
          url: endpoints.BugReports.REPORT_ID(id),
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) => [{ type: BUG_REPORTS.BUG_REPORT }],
      ...TransformedErrorResponseWithNotification(),
    }),
    postBugReportApi: builder.mutation<
      { id: string },
      {
        body: {
          report: Partial<IBugReport>;
          message: string;
          filesToUpload: IAppFile[];
        };
      }
    >({
      query({ body }) {
        return {
          url: '',
          method: HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: BUG_REPORTS.BUG_REPORTS,
              id: REDUX_CACHE.LIST,
            },
            {
              type: BUG_REPORTS.BUG_REPORT,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    postBugReportMessageApi: builder.mutation<
      void,
      {
        reportId: string;
        body: {
          message: IBugReportMessage;
          filesToUpload: IAppFile[];
        };
      }
    >({
      query({ reportId, body }) {
        return {
          url: endpoints.BugReports.MESSAGE(reportId),
          method: HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: BUG_REPORTS.BUG_REPORT,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    postBugReportStatusApi: builder.mutation<
      void,
      {
        reportId: string;
        body: { status: BugReportStatus };
      }
    >({
      query({ reportId, body }) {
        return {
          url: endpoints.BugReports.STATUS(reportId),
          method: HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: BUG_REPORTS.BUG_REPORTS,
            },
            {
              type: BUG_REPORTS.BUG_REPORT,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
  }),
});

export const {
  useGetMyBugReportsQuery: useGetMyBugReports,
  useGetBugReportTypesQuery: useGetBugReportTypes,
  useGetBugReportByIdApiQuery: useGetBugReportById,
  usePostBugReportApiMutation: usePostBugReport,
  usePostBugReportMessageApiMutation: usePostBugReportMessage,
  usePostBugReportStatusApiMutation: usePostBugReportStatus,
} = bugReportApi;
